import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmDashboardTodo } from '@euler/model/apdm';
import {
  ChevronRight,
  LucideIcon,
  MessageSquareText,
  RotateCcw,
  TriangleAlert,
  UserPlus,
} from 'lucide-react-native';
import { memo } from 'react';
import { Pressable, View } from 'react-native';
import {
  ApdmCard,
  ApdmIconTile,
  ApdmSectionTitle,
} from '../components/ApdmSurface';

function Row({
  icon,
  color,
  text,
  note,
  count,
  onPress,
  first,
}: {
  icon: LucideIcon;
  color: string;
  text: string;
  note?: string;
  count?: number;
  onPress?: () => void;
  first: boolean;
}) {
  const { rpx } = useResponsiveContext();
  return (
    <Pressable
      accessibilityRole={onPress ? 'button' : undefined}
      disabled={!onPress}
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: rpx(12),
        paddingHorizontal: rpx(16),
        paddingVertical: rpx(12),
        borderTopWidth: first ? 0 : 1,
        borderTopColor: aiColors.border,
        opacity: pressed ? 0.8 : 1,
      })}
    >
      <ApdmIconTile icon={icon} color={color} size={36} />
      <View style={{ flex: 1 }}>
        <Typography.Text size={rpx(14)} color={aiColors.foreground}>
          {text}
        </Typography.Text>
        {note ? (
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {note}
          </Typography.Text>
        ) : null}
      </View>
      {count != null ? (
        <View
          style={{
            paddingHorizontal: rpx(8),
            paddingVertical: rpx(2),
            borderRadius: rpx(10),
            backgroundColor: color,
          }}
        >
          <Typography.Text bold size={rpx(11)} color="#fff">
            {count}
          </Typography.Text>
        </View>
      ) : null}
      {onPress ? (
        <ChevronRight size={16} color={aiColors.mutedForeground} />
      ) : null}
    </Pressable>
  );
}

/** What waits for the caller: only the rows that have something. */
export const DashboardTodo = memo(
  ({
    todo,
    onReviews,
    onLeads,
    onUndelivered,
  }: {
    todo: ApdmDashboardTodo;
    onReviews: () => void;
    onLeads: () => void;
    onUndelivered: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const undelivered = todo.undelivered.reduce(
      (sum, item) => sum + item.count,
      0,
    );
    const rows = [
      todo.pendingReviews > 0 && {
        key: 'reviews',
        icon: MessageSquareText,
        color: aiColors.violet,
        text: `${todo.pendingReviews} 条客户回复待审核`,
        count: todo.pendingReviews,
        onPress: onReviews,
      },
      todo.unhandledLeads > 0 && {
        key: 'leads',
        icon: UserPlus,
        color: aiColors.green,
        text: `${todo.unhandledLeads} 条新线索还没人跟进`,
        count: todo.unhandledLeads,
        onPress: onLeads,
      },
      undelivered > 0 && {
        key: 'undelivered',
        icon: RotateCcw,
        color: aiColors.destructive,
        text: `${undelivered} 位客户没发出去，可以重试`,
        count: undelivered,
        onPress: onUndelivered,
      },
      todo.sendingBlocked && {
        key: 'sending',
        icon: TriangleAlert,
        color: aiColors.amber,
        text: '发送账号未在线，消息暂停发送',
        note: '账号连接后自动恢复',
      },
    ].filter(row => !!row) as Array<{
      key: string;
      icon: LucideIcon;
      color: string;
      text: string;
      note?: string;
      count?: number;
      onPress?: () => void;
    }>;
    return (
      <ApdmCard style={{ flex: 1 }}>
        <View style={{ paddingHorizontal: rpx(16), paddingTop: rpx(14) }}>
          <ApdmSectionTitle
            title="待处理"
            note={rows.length ? '需要你看一下' : undefined}
          />
        </View>
        <View style={{ marginTop: rpx(6) }}>
          {rows.length ? (
            rows.map(({ key, ...row }, index) => (
              <Row key={key} first={index === 0} {...row} />
            ))
          ) : (
            <Typography.Text
              size={rpx(13)}
              color={aiColors.mutedForeground}
              style={{ padding: rpx(16) }}
            >
              暂时没有要处理的事
            </Typography.Text>
          )}
        </View>
      </ApdmCard>
    );
  },
);
